import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CCard,
  CCardBody,
  CCardFooter,
  CCardText,
  CCardTitle,
} from "@coreui/react-pro";
import CIcon from "@coreui/icons-react";
import { cilChevronRight } from "@coreui/icons";
import type { TradingStrategyDto } from "../../../api/hooks/useTradingStrategies";
import type { CustomerMembershipDto } from "../../../api/hooks/useMemberships";
import { formatDateShort } from "../../../lib/dates";
import { formatMembershipNames } from "../../../lib/membership-copy";
import { myStrategyDetailPath } from "../../../routes/route-paths";
import { MembershipBadgeStack } from "./MembershipBadge";
import { StrategyIdentityBand } from "./StrategyIdentityBand";
import { StrategyCurve } from "./StrategyCurve";
import { StrategySidesTable } from "./StrategySidesTable";

/**
 * A strategy one or more of the customer's memberships include. Shaped like the Strategies card: each side opens its
 * own page, and the footer names what gives access.
 */
export function IncludedStrategyCard({
  sides,
  memberships,
}: {
  /** The sides the memberships include, in side order. The first is the one the card shows and opens. */
  sides: TradingStrategyDto[];
  memberships: CustomerMembershipDto[];
}) {
  const navigate = useNavigate();
  const [hovered, setHovered] = useState(false);

  const strategy = sides[0];

  const detailPath = myStrategyDetailPath(strategy.id);
  const names = formatMembershipNames(memberships);
  const endsAt = accessEndsAt(memberships);

  const cardStyle: React.CSSProperties = {
    cursor: "pointer",
    transition: "transform 120ms ease, box-shadow 120ms ease",
    transform: hovered ? "translateY(-2px)" : "none",
    boxShadow: hovered ? "0 0.5rem 1rem rgba(0,0,0,0.08)" : undefined,
  };

  const handleActivate = () => navigate(detailPath);
  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleActivate();
    }
  };

  return (
    <CCard
      className="h-100"
      style={cardStyle}
      role="button"
      tabIndex={0}
      onClick={handleActivate}
      onKeyDown={handleKeyDown}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      aria-label={`View ${strategy.publicName}, included in ${names}`}
    >
      <StrategyIdentityBand strategy={strategy} />
      <CCardBody className="d-flex flex-column">
        <CCardTitle>{strategy.publicName}</CCardTitle>
        <CCardText
          className="text-body-secondary"
          style={{
            display: "-webkit-box",
            WebkitLineClamp: 3,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
          }}
        >
          {strategy.description}
        </CCardText>
        <div className="mt-auto">
          <StrategyCurve strategy={strategy} emphasised={hovered} />
          <StrategySidesTable sides={sides} sideHref={(side) => myStrategyDetailPath(side.id)} />
        </div>
      </CCardBody>
      <CCardFooter className="bg-body-tertiary d-flex align-items-center gap-2">
        <MembershipBadgeStack memberships={memberships} />
        <small className="fw-semibold text-truncate flex-grow-1">{names}</small>
        {endsAt && (
          <small className="text-warning-emphasis fw-semibold flex-shrink-0">
            Until {formatDateShort(endsAt)}
          </small>
        )}
        <CIcon
          icon={cilChevronRight}
          size="xl"
          aria-hidden
          style={{
            opacity: hovered ? 0.95 : 0.45,
            transform: hovered ? "translateX(2px)" : "none",
            transition: "opacity 120ms ease, transform 120ms ease",
            color: "var(--cui-secondary-color)",
          }}
        />
      </CCardFooter>
    </CCard>
  );
}

/**
 * When access to the strategy stops: the last of its memberships' end dates, and only when every one of them
 * is ending. While any runs on, so does access.
 */
function accessEndsAt(memberships: CustomerMembershipDto[]): string | null {
  const endDates = memberships
    .map((membership) => membership.activeTo)
    .filter((activeTo): activeTo is string => activeTo !== null);
  const everyOneEnds = endDates.length === memberships.length;
  if (!everyOneEnds || endDates.length === 0) {
    return null;
  }

  return endDates.reduce((latest, endDate) =>
    Date.parse(endDate) > Date.parse(latest) ? endDate : latest,
  );
}
